import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardExpandToggler, CardHeader } from './../../components/card/card.jsx';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { RefreshCw, Users, Activity, Shield, TrendingUp, BarChart3, Clock, AlertTriangle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import statisticsService from '../../services/statisticsService';

const Statistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  // AI Costs state
  const [aiCosts, setAICosts] = useState(null);
  const [aiLoading, setAILoading] = useState(false);
  const [aiError, setAIError] = useState('');
  // AI filters & sorting state
  const [aiPeriod, setAIPeriod] = useState('30d'); // '7d' | '30d' | '90d' | 'custom'
  const [aiFrom, setAIFrom] = useState('');
  const [aiTo, setAITo] = useState('');
  const [aiOrgSortBy, setAIOrgSortBy] = useState('cost_total_usd');
  const [aiOrgSortDir, setAIOrgSortDir] = useState('desc');
  const [aiUserSortBy, setAIUserSortBy] = useState('cost_total_usd');
  const [aiUserSortDir, setAIUserSortDir] = useState('desc');

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Starting to fetch statistics...');
      
      const [statsResponse, logsResponse, attemptsResponse] = await Promise.all([
        statisticsService.getCombinedStatistics(),
        statisticsService.getCombinedActivityLogs(null, 50, 0),
        statisticsService.getCombinedLoginAttempts(50, 0)
      ]);
    
      // Handle different response structures
      const statsData = statsResponse?.data || statsResponse || {};
      const logsData = logsResponse?.data || logsResponse || [];
      const attemptsData = attemptsResponse?.data || attemptsResponse || [];
     
      // Ensure we have valid data before setting state
      if (statsData && typeof statsData === 'object') {
        setStatistics(statsData);
        console.log('Statistics set successfully:', statsData);
      } else {
        console.error('Invalid statistics data received:', statsData);
        setStatistics({});
      }
      
      setActivityLogs(Array.isArray(logsData) ? logsData : []);
      setLoginAttempts(Array.isArray(attemptsData) ? attemptsData : []);
      setError('');
    } catch (err) {
      console.error('Statistics fetch error:', err);
      setError('Failed to fetch statistics: ' + err.message);
      // Set default empty data on error
      setStatistics({});
      setActivityLogs([]);
      setLoginAttempts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch AI Costs lazily when the tab is opened
  const fetchAICosts = async () => {
    setAILoading(true);
    setAIError('');
    try {
      const params = aiPeriod === 'custom' ? (aiFrom && aiTo ? { from: aiFrom, to: aiTo } : {}) : { period: aiPeriod };
      const res = await statisticsService.getAICostAnalytics(params);
      const data = res?.data || res || {};
      setAICosts(data);
    } catch (e) {
      setAIError(e?.message || 'Failed to fetch AI cost analytics');
      setAICosts(null);
    } finally {
      setAILoading(false);
    }
  };

  const applyAIFilters = () => {
    if (activeTab === 'ai') {
      fetchAICosts();
    } else {
      setActiveTab('ai');
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  useEffect(() => {
    if (activeTab === 'ai' && aiCosts === null && !aiLoading) {
      fetchAICosts();
    }
  }, [activeTab]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '16rem' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h1">User Statistics & Analytics</h1>
        <Button onClick={fetchStatistics} variant="outline">
          <RefreshCw className="me-2" />
          Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="row">
        <div className="col-md-6 col-lg-3 mb-4" key="1">
        <Card className='mb-3'>
          <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
            <CardHeader className="fw-medium">Total Users</CardHeader>
            <Users className="text-muted" />
          </CardHeader>
          <CardBody>
            <div className="h2 fw-bold">{statistics?.total_users || 0}</div>
          </CardBody>
        </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
        <Card>
          <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
            <CardHeader className="fw-medium">Active Sessions</CardHeader>
            <Activity className="text-muted" />
          </CardHeader>
          <CardBody>
            <div className="h2 fw-bold">{statistics?.active_sessions || 0}</div>
          </CardBody>
        </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
        <Card>
          <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
            <CardHeader className="fw-medium">Login Success Rate (24h)</CardHeader>
            <Shield className="text-muted" />
          </CardHeader>
          <CardBody>
            <div className="h2 fw-bold">
              {statistics?.login_attempts_24h?.total > 0
                ? Math.round((statistics.login_attempts_24h.successful / statistics.login_attempts_24h.total) * 100)
                : 0}%
            </div>
            <p className="small text-muted">
              {statistics?.login_attempts_24h?.successful || 0} / {statistics?.login_attempts_24h?.total || 0} attempts
            </p>
          </CardBody>
        </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
        <Card>
          <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
            <CardHeader className="fw-medium">Failed Logins (24h)</CardHeader>
            <TrendingUp className="text-muted" />
          </CardHeader>
          <CardBody>
            <div className="h2 fw-bold text-danger">
              {statistics?.login_attempts_24h?.failed || 0}
            </div>
          </CardBody>
        </Card>
        </div>
      </div>

      {/* Pentest Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-6 col-lg-3 mb-4">
          <Card>
            <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
              <CardHeader className="fw-medium">Total Scans</CardHeader>
              <Shield className="text-muted" />
            </CardHeader>
            <CardBody>
              <div className="h2 fw-bold">{statistics?.pentest?.total_scans || 0}</div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
          <Card>
            <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
              <CardHeader className="fw-medium">Completed Scans</CardHeader>
              <Activity className="text-muted" />
            </CardHeader>
            <CardBody>
              <div className="h2 fw-bold text-success">
                {statistics?.pentest?.scans_by_status?.completed || 0}
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
          <Card>
            <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
              <CardHeader className="fw-medium">Failed Scans</CardHeader>
              <TrendingUp className="text-muted" />
            </CardHeader>
            <CardBody>
              <div className="h2 fw-bold text-danger">
                {statistics?.pentest?.scans_by_status?.failed || 0}
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6 col-lg-3 mb-4">
          <Card>
            <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
              <CardHeader className="fw-medium">Unique Scan Users</CardHeader>
              <Users className="text-muted" />
            </CardHeader>
            <CardBody>
              <div className="h2 fw-bold">{statistics?.pentest?.unique_users || 0}</div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="nav-tabs-cp mb-3">
          <TabsTrigger value="overview"><i className="bi bi-speedometer2"></i> <span>Overview</span></TabsTrigger>
          <TabsTrigger value="activity"><i className="bi bi-activity"></i> <span>Activity Trends</span></TabsTrigger>
          <TabsTrigger value="users"><i className="bi bi-people"></i> <span>User Analytics</span></TabsTrigger>
          <TabsTrigger value="pentest"><i className="bi bi-shield-lock"></i> <span>Pentest Analytics</span></TabsTrigger>
          <TabsTrigger value="login"><i className="bi bi-box-arrow-in-right"></i> <span>Login Analytics</span></TabsTrigger>
          <TabsTrigger value="security"><i className="bi bi-shield"></i> <span>Security</span></TabsTrigger>
          <TabsTrigger value="ai"><i className="bi bi-currency-dollar"></i> <span>AI Costs</span></TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mb-4">
          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* Users by Role */}
              <Card>
                <CardHeader>
                  <CardHeader>Users by Role</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(statistics?.users_by_role || {}).map(([role, count]) => ({
                          name: role,
                          value: count
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(statistics?.users_by_role || {}).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Top IP Addresses */}
              <Card>
                <CardHeader>
                  <CardHeader>Top IP Addresses (7 days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statistics?.top_ip_addresses || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="ip_address" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mb-4">
          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* CPAI Activity by Day */}
              <Card>
                <CardHeader>
                  <CardHeader>CPAI Daily Activity (30 days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={statistics?.activity_by_day || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={formatDate} />
                      <YAxis />
                      <Tooltip labelFormatter={formatDate} />
                      <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Pentest Activity by Day */}
              <Card>
                <CardHeader>
                  <CardHeader>Pentest Daily Activity (30 days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={statistics?.pentest?.activity_by_day || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={formatDate} />
                      <YAxis />
                      <Tooltip labelFormatter={formatDate} />
                      <Line type="monotone" dataKey="count" stroke="#ff7300" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Top Endpoints */}
          <Card>
            <CardHeader>
              <CardHeader>Most Accessed Endpoints (7 days)</CardHeader>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statistics?.top_endpoints || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="endpoint" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mb-4">
          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* Most Active CPAI Users */}
              <Card>
                <CardHeader>
                  <CardHeader>Most Active CPAI Users (7 days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.most_active_users?.map((user, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">#{index + 1}</Badge>
                          <span className="fw-medium">{user.username}</span>
                        </div>
                        <Badge>{user.activity_count} actions</Badge>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Most Active Pentest Users */}
              <Card>
                <CardHeader>
                  <CardHeader>Most Active Pentest Users (7 days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.pentest?.most_active_users?.map((user, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">#{index + 1}</Badge>
                          <span className="fw-medium">{user.username}</span>
                        </div>
                        <Badge>{user.activity_count} scans</Badge>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          {/* Combined Users by Role */}
          <Card>
            <CardHeader>
              <CardHeader>User Distribution by Role</CardHeader>
            </CardHeader>
            <CardBody>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <h4 className="fw-medium mb-3">CPAI Users</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(statistics?.users_by_role || {}).map(([role, count]) => ({
                          name: role,
                          value: count
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.entries(statistics?.users_by_role || {}).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="col-lg-6 mb-4">
                  <h4 className="fw-medium mb-3">Pentest Users</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={Object.entries(statistics?.pentest?.users_by_role || {}).map(([role, count]) => ({
                          name: role,
                          value: count
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={60}
                        fill="#82ca9d"
                        dataKey="value"
                      >
                        {Object.entries(statistics?.pentest?.users_by_role || {}).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="pentest" className="mb-4">
          <div className="row">
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Scans by Status</CardHeader>
                  <BarChart3 className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div style={{ height: '200px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Completed', value: statistics?.pentest?.scans_by_status?.completed || 0, fill: '#22c55e' },
                            { name: 'Failed', value: statistics?.pentest?.scans_by_status?.failed || 0, fill: '#ef4444' },
                            { name: 'Running', value: statistics?.pentest?.scans_by_status?.running || 0, fill: '#3b82f6' }
                          ]}
                          cx="50%"
                          cy="50%"
                          outerRadius={60}
                          dataKey="value"
                        >
                          <Tooltip />
                        </Pie>
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Average Scan Duration</CardHeader>
                  <Clock className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">
                    {statistics?.pentest?.avg_scan_duration_minutes ? 
                      `${statistics.pentest.avg_scan_duration_minutes.toFixed(1)} min` : 
                      'N/A'
                    }
                  </div>
                  <p className="small text-muted mt-1">
                    Average time per scan
                  </p>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Success Rate</CardHeader>
                  <TrendingUp className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">
                    {statistics?.pentest?.scans_by_status ? 
                      `${Math.round((statistics.pentest.scans_by_status.completed || 0) / 
                      (statistics.pentest.total_scans || 1) * 100)}%` : 
                      'N/A'
                    }
                  </div>
                  <p className="small text-muted mt-1">
                    Successful scans
                  </p>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Failed Scans</CardHeader>
                  <AlertTriangle className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold text-danger">
                    {statistics?.pentest?.failed_scans_details?.length || 0}
                  </div>
                  <p className="small text-muted mt-1">
                    Recent failures
                  </p>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Scan Duration Distribution</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ height: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statistics?.pentest?.scan_duration_distribution || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="duration_range" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8884d8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Top Target URLs</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ height: '300px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statistics?.pentest?.top_target_urls?.slice(0, 8) || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="target_url" angle={-45} textAnchor="end" height={100} fontSize={10} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="scan_count" fill="#22c55e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Most Active Users</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.pentest?.most_active_users?.slice(0, 8).map((user, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-light rounded mb-2">
                        <div>
                          <span className="fw-medium">{user.username}</span>
                          <p className="small text-muted">
                            Avg: {user.avg_duration_minutes ? `${user.avg_duration_minutes.toFixed(1)} min` : 'N/A'}
                          </p>
                        </div>
                        <span className="fw-bold">{user.scan_count} scans</span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>User Success Rates</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.pentest?.user_success_rates?.slice(0, 8).map((user, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-2 bg-light rounded mb-2">
                        <div>
                          <span className="fw-medium">{user.username}</span>
                          <p className="small text-muted">
                            {user.successful_scans}/{user.total_scans} scans
                          </p>
                        </div>
                        <span className={`fw-bold ${
                          user.success_rate >= 80 ? 'text-success' : 
                          user.success_rate >= 60 ? 'text-warning' : 'text-danger'
                        }`}>
                          {user.success_rate}%
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          {(statistics?.pentest?.performance_metrics || statistics?.pentest?.failed_scans_analysis) && (
                <div className="row">
                  {statistics?.pentest?.performance_metrics && (
                    <div className="col-lg-6 mb-4">
                      <Card>
                        <CardHeader>
                          <CardHeader>Performance Metrics</CardHeader>
                        </CardHeader>
                        <CardBody>
                          <div className="d-flex flex-column">
                            {statistics.pentest.performance_metrics.longest_scans?.length > 0 && (
                              <div className="mb-3">
                                <h4 className="fw-semibold mb-2 text-danger">Longest Scans</h4>
                                <div className="d-flex flex-column">
                                  {statistics.pentest.performance_metrics.longest_scans.slice(0, 3).map((scan, index) => (
                                    <div key={index} className="p-2 bg-danger-subtle border border-danger rounded mb-2">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <span className="small fw-medium">{scan.target_url}</span>
                                        <span className="small text-danger fw-bold">
                                          {scan.duration_minutes?.toFixed(1)} min
                                        </span>
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {scan.username} • {new Date(scan.start_time).toLocaleDateString()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {statistics.pentest.performance_metrics.fastest_scans?.length > 0 && (
                              <div>
                                <h4 className="fw-semibold mb-2 text-success">Fastest Scans</h4>
                                <div className="d-flex flex-column">
                                  {statistics.pentest.performance_metrics.fastest_scans.slice(0, 3).map((scan, index) => (
                                    <div key={index} className="p-2 bg-success-subtle border border-success rounded mb-2">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <span className="small fw-medium">{scan.target_url}</span>
                                        <span className="small text-success fw-bold">
                                          {scan.duration_minutes?.toFixed(1)} min
                                        </span>
                                      </div>
                                      <div className="small text-muted mt-1">
                                        {scan.username} • {new Date(scan.start_time).toLocaleDateString()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  )}

                  {statistics?.pentest?.failed_scans_analysis && (
                    <div className="col-lg-6 mb-4">
                      <Card>
                        <CardHeader>
                          <CardHeader>Failed Scans Analysis</CardHeader>
                        </CardHeader>
                        <CardBody>
                          <div className="d-flex flex-column">
                            {statistics.pentest.failed_scans_analysis.common_errors?.length > 0 && (
                              <div className="mb-3">
                                <h4 className="fw-semibold mb-2">Common Error Patterns</h4>
                                <div className="d-flex flex-column">
                                  {statistics.pentest.failed_scans_analysis.common_errors.slice(0, 5).map((error, index) => (
                                    <div key={index} className="p-2 bg-light rounded mb-2">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span className="small fw-medium text-truncate">{error.error_pattern}</span>
                                        <span className="small text-danger fw-bold">{error.count}x</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {statistics.pentest.failed_scans_analysis.failure_by_user?.length > 0 && (
                              <div>
                                <h4 className="fw-semibold mb-2">Failures by User</h4>
                                <div className="d-flex flex-column">
                                  {statistics.pentest.failed_scans_analysis.failure_by_user.slice(0, 5).map((user, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center p-2 bg-light rounded mb-2">
                                      <span className="small fw-medium">{user.username}</span>
                                      <span className="small text-danger fw-bold">{user.failed_count} failures</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  )}
                </div>
              )}

              {statistics?.pentest?.failed_scans_details?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardHeader>Recent Failed Scans</CardHeader>
                  </CardHeader>
                  <CardBody>
                    <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                      {statistics.pentest.failed_scans_details.slice(0, 10).map((scan, index) => (
                        <div key={index} className="p-3 border border-danger bg-danger-subtle rounded mb-3">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="fw-medium">{scan.target_url}</span>
                            <span className="small text-muted">
                              {new Date(scan.start_time).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="small text-muted mb-1">
                            User: {scan.username} | Duration: {scan.duration_minutes ? `${scan.duration_minutes.toFixed(1)} min` : 'N/A'}
                          </div>
                          {scan.error && (
                            <div className="small text-danger bg-danger-subtle p-2 rounded mt-2">
                              Error: {scan.error}
                            </div>
                          )}
                          {scan.options && (
                            <div className="small text-body-secondary mt-1">
                              Options: {JSON.stringify(scan.options)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}
        </TabsContent>

        <TabsContent value="login" className="mb-4">
          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* CPAI Login Attempts Chart */}
              <Card>
                <CardHeader>
                  <CardHeader>CPAI Login Attempts (Last 7 Days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={loginAttempts}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="successful" stroke="#10b981" strokeWidth={2} />
                      <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Pentest Login Attempts Chart */}
              <Card>
                <CardHeader>
                  <CardHeader>Pentest Login Attempts (Last 7 Days)</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                   <LineChart data={loginAttempts?.pentest || []}>
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis dataKey="date" />
                     <YAxis />
                     <Tooltip />
                     <Legend />
                     <Line type="monotone" dataKey="successful" stroke="#10b981" strokeWidth={2} />
                     <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} />
                   </LineChart>
                 </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* Top IP Addresses - CPAI */}
              <Card>
                <CardHeader>
                  <CardHeader>Top IP Addresses - CPAI (Failed Logins)</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.top_ips?.map((ip, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">#{index + 1}</Badge>
                          <span className="font-monospace">{ip.ip_address}</span>
                        </div>
                        <Badge variant="destructive">{ip.failed_attempts} failed</Badge>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Top IP Addresses - Pentest */}
              <Card>
                <CardHeader>
                  <CardHeader>Top IP Addresses - Pentest (Failed Logins)</CardHeader>
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column">
                    {statistics?.pentest?.top_ips?.map((ip, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-3">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">#{index + 1}</Badge>
                          <span className="font-monospace">{ip.ip_address}</span>
                        </div>
                        <Badge variant="destructive">{ip.failed_attempts} failed</Badge>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="security" className="mb-4">
          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* Recent CPAI Login Attempts */}
              <Card>
                <CardHeader>
                  <CardHeader>Recent CPAI Login Attempts</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                    {(loginAttempts || []).map((attempt, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                        <div className="d-flex align-items-center">
                          <Badge variant={attempt.success ? "default" : "destructive"} className="me-3">
                            {attempt.success ? "Success" : "Failed"}
                          </Badge>
                          <span className="fw-medium me-3">{attempt.username}</span>
                          <span className="small text-muted">{attempt.ip_address}</span>
                        </div>
                        <span className="small text-muted">
                          {formatDateTime(attempt.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Recent Pentest Login Attempts */}
              <Card>
                <CardHeader>
                  <CardHeader>Recent Pentest Login Attempts</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                    {(loginAttempts?.pentest || []).map((attempt, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                        <div className="d-flex align-items-center">
                          <Badge variant={attempt.success ? "default" : "destructive"} className="me-3">
                            {attempt.success ? "Success" : "Failed"}
                          </Badge>
                          <span className="fw-medium me-3">{attempt.username}</span>
                          <span className="small text-muted">{attempt.ip_address}</span>
                        </div>
                        <span className="small text-muted">
                          {formatDateTime(attempt.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 mb-4">
              {/* Recent CPAI Activity Logs */}
              <Card>
                <CardHeader>
                  <CardHeader>Recent CPAI Activity</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                    {(activityLogs || []).map((log, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">{log.request_method}</Badge>
                          <span className="fw-medium me-3">{log.username}</span>
                          <span className="small text-muted me-3">{log.endpoint}</span>
                          <Badge variant={log.response_status < 400 ? "default" : "destructive"}>
                            {log.response_status}
                          </Badge>
                        </div>
                        <span className="small text-muted">
                          {formatDateTime(log.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-6 mb-4">
              {/* Recent Pentest Activity Logs */}
              <Card>
                <CardHeader>
                  <CardHeader>Recent Pentest Activity</CardHeader>
                </CardHeader>
                <CardBody>
                  <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                    {(activityLogs?.pentest || []).map((log, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3 border rounded mb-2">
                        <div className="d-flex align-items-center">
                          <Badge variant="outline" className="me-3">{log.request_method}</Badge>
                          <span className="fw-medium me-3">{log.username}</span>
                          <span className="small text-muted me-3">{log.endpoint}</span>
                          <Badge variant={log.response_status < 400 ? "default" : "destructive"}>
                            {log.response_status}
                          </Badge>
                        </div>
                        <span className="small text-muted">
                          {formatDateTime(log.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </TabsContent>
      <TabsContent value="ai" className="mb-4">
        {/* AI Filters Toolbar */}
        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div>
            <label className="form-label mb-1">Period</label>
            <select className="form-select" value={aiPeriod} onChange={(e) => setAIPeriod(e.target.value)}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {aiPeriod === 'custom' && (
            <>
              <div>
                <label className="form-label mb-1">From</label>
                <input type="date" className="form-control" value={aiFrom} onChange={(e) => setAIFrom(e.target.value)} />
              </div>
              <div>
                <label className="form-label mb-1">To</label>
                <input type="date" className="form-control" value={aiTo} onChange={(e) => setAITo(e.target.value)} />
              </div>
            </>
          )}
          <div className="ms-auto d-flex align-items-end">
            <Button variant="outline" onClick={applyAIFilters} disabled={aiPeriod === 'custom' && (!aiFrom || !aiTo)}>
              <RefreshCw className="me-2" /> Apply
            </Button>
          </div>
        </div>

        {aiLoading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: '12rem' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading AI cost analytics...</span>
          </div>
        ) : aiError ? (
          <Alert variant="destructive">
            <AlertDescription>{aiError}</AlertDescription>
          </Alert>
        ) : aiCosts && aiCosts.available === false ? (
          <Alert>
            <AlertDescription>
              {aiCosts.message || 'AI cost analytics are not available. Configure Models & Pricing in Admin Settings.'}
            </AlertDescription>
          </Alert>
        ) : aiCosts ? (
          <>
          {/* Org summary cards */}
          <div className="row mb-2">
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Total AI Cost</CardHeader>
                  <i className="bi bi-currency-dollar text-muted"></i>
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">
                    {aiCosts.currency || 'USD'} {Number(aiCosts?.totals?.cost_total_usd || 0).toFixed(2)}
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Organizations</CardHeader>
                  <Users className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">{(aiCosts?.per_org || []).length}</div>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Total Users</CardHeader>
                  <Users className="text-muted" />
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">{(aiCosts?.per_org || []).reduce((sum, o) => sum + (o.users_count || 0), 0)}</div>
                </CardBody>
              </Card>
            </div>
            <div className="col-md-6 col-lg-3 mb-4">
              <Card>
                <CardHeader className="d-flex flex-row align-items-center justify-content-between pb-2">
                  <CardHeader className="fw-medium">Avg Cost / 1M tokens</CardHeader>
                  <i className="bi bi-graph-up text-muted"></i>
                </CardHeader>
                <CardBody>
                  <div className="h2 fw-bold">
                    {(() => {
                      const inUSD = Number(aiCosts?.totals?.cost_in_usd || 0);
                      const outUSD = Number(aiCosts?.totals?.cost_out_usd || 0);
                      const inTok = Number(aiCosts?.totals?.tokens_in || 0);
                      const outTok = Number(aiCosts?.totals?.tokens_out || 0);
                      const avg = ((inUSD + outUSD) / Math.max(1, (inTok + outTok))) * 1000000;
                      return `${(avg || 0).toFixed(2)} ${aiCosts.currency || 'USD'}`;
                    })()}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Cost by Model</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(aiCosts?.by_model || {}).map(([model, v]) => ({ name: model, value: Number(v?.cost_total_usd || 0) }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {Object.keys(aiCosts?.by_model || {}).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>

            <div className="col-lg-6 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Top Organizations by Cost</CardHeader>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={(aiCosts?.per_org || []).slice(0, 10).map((r) => ({ org: r.org, cost: Number(r.cost_total_usd || 0) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="org" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="cost" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>
            </div>

            <div className="col-12 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Per-Organization Breakdown</CardHeader>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Sort by:</span>
                    <select className="form-select form-select-sm" style={{ width: 160 }} value={aiOrgSortBy} onChange={(e) => setAIOrgSortBy(e.target.value)}>
                      <option value="cost_total_usd">Cost</option>
                      <option value="tokens_in">Prompt Tokens</option>
                      <option value="tokens_out">Completion Tokens</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setAIOrgSortDir(aiOrgSortDir === 'desc' ? 'asc' : 'desc')}>
                      {aiOrgSortDir === 'desc' ? 'Desc' : 'Asc'}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Organization</th>
                          <th className="text-end">Users</th>
                          <th className="text-end">Prompt Tokens</th>
                          <th className="text-end">Completion Tokens</th>
                          <th className="text-end">Total Cost ({aiCosts.currency || 'USD'})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(aiCosts?.per_org || [])]
                          .sort((a, b) => {
                            const field = aiOrgSortBy;
                            const av = Number(a?.[field] || 0);
                            const bv = Number(b?.[field] || 0);
                            return aiOrgSortDir === 'desc' ? bv - av : av - bv;
                          })
                          .map((r, idx) => (
                            <tr key={idx}>
                              <td className="fw-medium">{r.org}</td>
                              <td className="text-end">{Number(r.users_count || 0).toLocaleString()}</td>
                              <td className="text-end">{Number(r.tokens_in || 0).toLocaleString()}</td>
                              <td className="text-end">{Number(r.tokens_out || 0).toLocaleString()}</td>
                              <td className="text-end">{Number(r.cost_total_usd || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-12 mb-4">
              <Card>
                <CardHeader>
                  <CardHeader>Per-User Breakdown</CardHeader>
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Sort by:</span>
                    <select className="form-select form-select-sm" style={{ width: 160 }} value={aiUserSortBy} onChange={(e) => setAIUserSortBy(e.target.value)}>
                      <option value="cost_total_usd">Cost</option>
                      <option value="tokens_in">Prompt Tokens</option>
                      <option value="tokens_out">Completion Tokens</option>
                    </select>
                    <Button variant="outline" size="sm" onClick={() => setAIUserSortDir(aiUserSortDir === 'desc' ? 'asc' : 'desc')}>
                      {aiUserSortDir === 'desc' ? 'Desc' : 'Asc'}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Organization</th>
                          <th className="text-end">Prompt Tokens</th>
                          <th className="text-end">Completion Tokens</th>
                          <th className="text-end">Total Cost ({aiCosts.currency || 'USD'})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(aiCosts?.per_user || [])]
                          .sort((a, b) => {
                            const field = aiUserSortBy;
                            const av = Number(a?.[field] || 0);
                            const bv = Number(b?.[field] || 0);
                            return aiUserSortDir === 'desc' ? bv - av : av - bv;
                          })
                          .map((r, idx) => (
                            <tr key={idx}>
                              <td className="fw-medium">{r.user}</td>
                              <td>{r.org}</td>
                              <td className="text-end">{Number(r.tokens_in || 0).toLocaleString()}</td>
                              <td className="text-end">{Number(r.tokens_out || 0).toLocaleString()}</td>
                              <td className="text-end">{Number(r.cost_total_usd || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
          </>
        ) : null}
      </TabsContent>
    </Tabs>
  </div>
);
};
export default Statistics;
